import { useCallback, useEffect, useMemo, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
} from 'recharts';

import { supabase } from '../providers/supabase/supabase';

type EmailEventRow = {
    id: string;
    created_at: string;
    event_type: string;
    sequence_step: string | null;
    prospect_id: number | null;
    email: string | null;
    reason: string | null;
};

type SummaryRow = {
    event_type: string;
    event_count: number;
};

type TimeseriesRow = {
    bucket: string;
    event_type: string;
    event_count: number;
};

type ReplyStats = {
    total_sent: number;
    replies: number;
    reply_rate: number;
};

type CountResponse = {
    count: number | null;
};

const SUMMARY_TYPES: Array<{ key: string; label: string }> = [
    { key: 'delivered', label: 'Delivered' },
    { key: 'open', label: 'Opens' },
    { key: 'click', label: 'Clicks' },
    { key: 'bounce', label: 'Bounces' },
    { key: 'dropped', label: 'Dropped' },
    { key: 'spamreport', label: 'Spam Reports' },
];

const RECENT_LIMIT = 75;
const SUMMARY_WINDOW_DAYS = 30;
const TIMESERIES_WINDOW_DAYS = 28;

export const DeliverabilityDashboard = () => {
    const [events, setEvents] = useState<EmailEventRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [summaryCounts, setSummaryCounts] = useState<Record<string, number>>({});
    const [timeseries, setTimeseries] = useState<TimeseriesRow[]>([]);
    const [replyStats, setReplyStats] = useState<ReplyStats | null>(null);
    const [opens24h, setOpens24h] = useState<number | null>(null);
    const [metricsLoading, setMetricsLoading] = useState(true);
    const [metricsError, setMetricsError] = useState<string | null>(null);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        const { data, error: fetchError } = await supabase
            .from('email_events_external')
            .select(
                'id, created_at, event_type, sequence_step, prospect_id, email, reason'
            )
            .order('created_at', { ascending: false })
            .limit(RECENT_LIMIT);

        if (fetchError) {
            setError(fetchError.message);
            setEvents([]);
        } else {
            setEvents(data ?? []);
        }
        setLoading(false);
    }, []);

    const fetchMetrics = useCallback(async () => {
        setMetricsLoading(true);
        setMetricsError(null);

        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

        const [summaryRes, timeseriesRes, replyRes, opens24hRes] = await Promise.all([
            supabase.rpc('email_event_summary', { days: SUMMARY_WINDOW_DAYS }),
            supabase.rpc('email_event_timeseries', {
                days: TIMESERIES_WINDOW_DAYS,
            }),
            supabase.rpc('email_reply_stats', { days: SUMMARY_WINDOW_DAYS }),
            supabase
                .from('email_events_external')
                .select('id', { head: true, count: 'exact' })
                .eq('event_type', 'open')
                .gte('created_at', twentyFourHoursAgo),
        ]);

        if (summaryRes.error) {
            setMetricsError(summaryRes.error.message);
        } else {
            const map =
                (summaryRes.data as SummaryRow[] | null)?.reduce<
                    Record<string, number>
                >((acc, row) => {
                    const key = row.event_type?.toLowerCase();
                    if (!key) return acc;
                    acc[key] = row.event_count;
                    return acc;
                }, {}) ?? {};
            setSummaryCounts(map);
        }

        if (timeseriesRes.error) {
            setMetricsError(prev => prev ?? timeseriesRes.error!.message);
        } else {
            setTimeseries((timeseriesRes.data as TimeseriesRow[] | null) ?? []);
        }

        if (replyRes.error) {
            setMetricsError(prev => prev ?? replyRes.error!.message);
        } else {
            const statsArray = replyRes.data as ReplyStats[] | null;
            const firstRow = statsArray?.[0] ?? null;
            setReplyStats(firstRow);
        }

        if (opens24hRes.error) {
            setMetricsError(prev => prev ?? opens24hRes.error!.message);
            setOpens24h(null);
        } else {
            setOpens24h((opens24hRes as CountResponse).count ?? 0);
        }

        setMetricsLoading(false);
    }, []);

    useEffect(() => {
        fetchEvents();
        fetchMetrics();
    }, [fetchEvents, fetchMetrics]);

    const opensByStep = useMemo(() => {
        return events.reduce<Record<string, number>>((acc, event) => {
            if (event.event_type.toLowerCase() !== 'open') return acc;
            const key = event.sequence_step ?? 'unknown';
            acc[key] = (acc[key] ?? 0) + 1;
            return acc;
        }, {});
    }, [events]);

    const recentDeliveries = useMemo(() => {
        return events.filter(event =>
            ['delivered', 'processed'].includes(event.event_type.toLowerCase())
        );
    }, [events]);

    const lastUpdated =
        events.length > 0
            ? new Date(events[0].created_at).toLocaleString()
            : null;

    const weeklySeries = useMemo(() => {
        const tracked = new Set(['delivered', 'open', 'click']);
        const map = new Map<
            string,
            { week: string; weekLabel: string; delivered: number; open: number; click: number }
        >();

        timeseries.forEach(row => {
            const key = row.event_type?.toLowerCase();
            if (!tracked.has(key)) return;
            const bucket = row.bucket;
            if (!map.has(bucket)) {
                const date = new Date(bucket);
                map.set(bucket, {
                    week: bucket,
                    weekLabel: date.toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                    }),
                    delivered: 0,
                    open: 0,
                    click: 0,
                });
            }
            const entry = map.get(bucket)!;
            entry[key as 'delivered' | 'open' | 'click'] = row.event_count;
        });

        return Array.from(map.values()).sort(
            (a, b) => new Date(a.week).getTime() - new Date(b.week).getTime()
        );
    }, [timeseries]);

    const handleRefresh = () => {
        fetchEvents();
        fetchMetrics();
    };

    const formatPercent = (value: number | undefined | null) =>
        `${((value ?? 0) * 100).toFixed(1)}%`;

    return (
        <Stack spacing={3} sx={{ py: 4 }}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
            >
                <Box>
                    <Typography variant="h4" component="h1">
                        Email Deliverability
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Live snapshot of SendGrid engagement (Freshly Baked
                        opens filtered out).
                    </Typography>
                    {lastUpdated && (
                        <Typography variant="caption" color="text.secondary">
                            Last updated {lastUpdated}
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    startIcon={
                        loading || metricsLoading ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <RefreshIcon />
                        )
                    }
                    onClick={handleRefresh}
                    disabled={loading || metricsLoading}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error">
                    Could not load email events: {error}
                </Alert>
            )}
            {metricsError && (
                <Alert severity="error">
                    Could not load deliverability metrics: {metricsError}
                </Alert>
            )}

            <Grid container spacing={3}>
                {SUMMARY_TYPES.map(({ key, label }) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={key}>
                        <Card>
                            <CardContent>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    {label}
                                </Typography>
                                <Typography variant="h4">
                                    {summaryCounts[key] ?? 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Reply Rate (30d)
                            </Typography>
                            <Typography variant="h4">
                                {formatPercent(replyStats?.reply_rate)}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {(replyStats?.replies ?? 0).toLocaleString()} replies /{' '}
                                {(replyStats?.total_sent ?? 0).toLocaleString()} sends
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Opens (last 24h)
                            </Typography>
                            <Typography variant="h4">
                                {opens24h ?? 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card>
                <CardContent>
                    <Typography variant="h6">
                        Weekly Engagement (last 4 weeks)
                    </Typography>
                    {weeklySeries.length === 0 ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Not enough data yet.
                        </Typography>
                    ) : (
                        <Box sx={{ height: 280, mt: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={weeklySeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="weekLabel" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="delivered"
                                        stroke="#1976d2"
                                        name="Delivered"
                                        strokeWidth={2}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="open"
                                        stroke="#2e7d32"
                                        name="Opens"
                                        strokeWidth={2}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="click"
                                        stroke="#ed6c02"
                                        name="Clicks"
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6">
                                Opens by Sequence Step
                            </Typography>
                            {Object.keys(opensByStep).length === 0 ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No opens captured yet.
                                </Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Step</TableCell>
                                                <TableCell align="right">
                                                    Opens
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(opensByStep)
                                                .sort(
                                                    (a, b) => b[1] - a[1]
                                                )
                                                .map(([step, value]) => (
                                                    <TableRow key={step}>
                                                        <TableCell>
                                                            {step}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {value}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6">
                                Recent Deliveries
                            </Typography>
                            {recentDeliveries.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No delivery events yet.
                                </Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Timestamp</TableCell>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Prospect</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recentDeliveries
                                                .slice(0, 10)
                                                .map(event => (
                                                    <TableRow key={event.id}>
                                                        <TableCell>
                                                            {new Date(
                                                                event.created_at
                                                            ).toLocaleString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {event.email ||
                                                                '—'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                event.event_type
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {event.prospect_id ??
                                                                '—'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Recent Activity ({events.length} events)
                    </Typography>
                    {loading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            py={4}
                        >
                            <CircularProgress />
                        </Box>
                    ) : events.length === 0 ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Waiting for SendGrid events...
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Timestamp</TableCell>
                                        <TableCell>Event</TableCell>
                                        <TableCell>Sequence Step</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Prospect</TableCell>
                                        <TableCell>Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {events.map(event => (
                                        <TableRow key={event.id}>
                                            <TableCell>
                                                {new Date(
                                                    event.created_at
                                                ).toLocaleString()}
                                            </TableCell>
                                            <TableCell>
                                                {event.event_type}
                                            </TableCell>
                                            <TableCell>
                                                {event.sequence_step ?? '—'}
                                            </TableCell>
                                            <TableCell>
                                                {event.email ?? '—'}
                                            </TableCell>
                                            <TableCell>
                                                {event.prospect_id ?? '—'}
                                            </TableCell>
                                            <TableCell>
                                                {event.reason ?? '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Stack>
    );
};
